<div class="audit-trail-container">
  <!-- Header & Controls -->
  <div class="audit-header">
    <div class="header-controls">
      <div class="header-actions">
        <button mat-button (click)="toggleAdvancedFilters()">
          <mat-icon>tune</mat-icon>
          {{ showAdvancedFilters ? 'Hide' : 'Show' }} Filters
        </button>

        <button mat-button (click)="toggleColumnSelector()">
          <mat-icon>view_column</mat-icon>
          Columns
        </button>
      </div>
    </div>
    
    <!-- Column Selector -->
    <div class="column-selector" *ngIf="showColumnSelector" [@slideDown]>
      <div class="column-selector-content">
        <div class="column-options">
          <div class="column-option" *ngFor="let column of allColumns">
            <mat-checkbox
              *ngIf="!column.required"
              [checked]="column.visible"
              (change)="toggleColumnVisibility(column.key)"
              [attr.aria-label]="'Toggle ' + column.label + ' column visibility'">
              {{ column.label }}
            </mat-checkbox>
          </div>
        </div>

        <div class="column-selector-actions">
          <button mat-button (click)="resetColumnsToDefault()" class="reset-button">
            <mat-icon>refresh</mat-icon>
            Reset to Default
          </button>
        </div>
      </div>
    </div>
    
    <!-- Filter Bar -->
    <div class="filter-bar" [formGroup]="filterForm">
      <!-- Quick Filters -->
      <div class="quick-filters">
        <!-- Time-based filters -->
        <div class="filter-chips-row">
          <button
            class="chip-button"
            [class.active]="quickFilters.ALL_TIME.active"
            (click)="applyQuickFilter('ALL_TIME')">
            {{ quickFilters.ALL_TIME.label }}
          </button>
          <button
            class="chip-button"
            [class.active]="quickFilters.WEEK.active"
            (click)="applyQuickFilter('WEEK')">
            {{ quickFilters.WEEK.label }}
          </button>
          <button
            class="chip-button"
            [class.active]="quickFilters.LAST_MONTH.active"
            (click)="applyQuickFilter('LAST_MONTH')">
            {{ quickFilters.LAST_MONTH.label }}
          </button>
        </div>

        <!-- Action and user-based filters -->
        <div class="filter-chips-row">
          <button 
            class="chip-button"
            [class.active]="quickFilters.MY_CHANGES.active"
            (click)="applyQuickFilter('MY_CHANGES')">
            {{ quickFilters.MY_CHANGES.label }}
          </button>
          <button 
            class="chip-button"
            [class.active]="quickFilters.CREATES_ONLY.active"
            (click)="applyQuickFilter('CREATES_ONLY')">
            {{ quickFilters.CREATES_ONLY.label }}
          </button>
          <button 
            class="chip-button"
            [class.active]="quickFilters.DELETES_ONLY.active"
            (click)="applyQuickFilter('DELETES_ONLY')">
            {{ quickFilters.DELETES_ONLY.label }}
          </button>
          <button 
            class="chip-button"
            [class.active]="quickFilters.UPDATES_ONLY.active"
            (click)="applyQuickFilter('UPDATES_ONLY')">
            {{ quickFilters.UPDATES_ONLY.label }}
          </button>
          
          <!-- Manual Changes Toggle as Chip -->
          <button 
            class="chip-button manual-changes-chip"
            [class.active]="filterForm.get('showSystemChanges')?.value"
            (click)="toggleSystemChanges()">
            <mat-icon class="chip-icon">build</mat-icon>
            Show Manual Changes
          </button>
        </div>
      </div>

      <!-- Advanced Filters -->
      <div class="advanced-filters" *ngIf="showAdvancedFilters" [@slideDown]>
        <!-- Row 1: Entity Types, From Date, To Date -->
        <div class="filter-row">
          <mat-form-field appearance="outline" class="filter-field">
            <mat-label>Record Types</mat-label>
            <mat-select formControlName="entityTypes" multiple>
              <mat-option *ngFor="let entityType of availableEntityTypes" [value]="entityType">
                {{ entityType }}
              </mat-option>
            </mat-select>
            <mat-hint>Filter by record types</mat-hint>
          </mat-form-field>

          <mat-form-field appearance="outline" class="filter-field">
            <mat-label>From Date</mat-label>
            <input matInput [matDatepicker]="fromPicker" formControlName="dateFrom">
            <mat-hint>Filter from this date</mat-hint>
            <mat-datepicker-toggle matIconSuffix [for]="fromPicker"></mat-datepicker-toggle>
            <mat-datepicker #fromPicker></mat-datepicker>
          </mat-form-field>

          <mat-form-field appearance="outline" class="filter-field">
            <mat-label>To Date</mat-label>
            <input matInput [matDatepicker]="toPicker" formControlName="dateTo">
            <mat-hint>Filter until this date</mat-hint>
            <mat-datepicker-toggle matIconSuffix [for]="toPicker"></mat-datepicker-toggle>
            <mat-datepicker #toPicker></mat-datepicker>
          </mat-form-field>
        </div>

        <!-- Row 2: Actions, Users, Search -->
        <div class="filter-row">
          <mat-form-field appearance="outline" class="filter-field">
            <mat-label>Actions</mat-label>
            <mat-select formControlName="actions" multiple>
              <mat-option *ngFor="let action of availableActions" [value]="action">
                <mat-icon [style.color]="getActionColor(action)">{{ getActionIcon(action) }}</mat-icon>
                {{ action }}
              </mat-option>
            </mat-select>
            <mat-hint>Filter by action types</mat-hint>
          </mat-form-field>

          <mat-form-field appearance="outline" class="filter-field">
            <mat-label>Users</mat-label>
            <mat-select formControlName="users" multiple>
              <mat-option *ngFor="let user of availableUsers" [value]="user">
                {{ user }}
              </mat-option>
            </mat-select>
            <mat-hint>Filter by specific users</mat-hint>
          </mat-form-field>

          <mat-form-field appearance="outline" class="filter-field">
            <mat-label>Search</mat-label>
            <input matInput formControlName="searchText" placeholder="Search audit entries...">
            <mat-icon matSuffix>search</mat-icon>
            <mat-hint>Search across all fields</mat-hint>
          </mat-form-field>
        </div>

        <!-- Row 3: Buttons only -->
        <div class="filter-row search-actions">
          <div class="search-buttons">
            <button mat-raised-button color="primary" (click)="searchAuditData()" class="search-button">
              <mat-icon>search</mat-icon>
              Search
            </button>
            <button mat-button (click)="clearFilters()" class="clear-button">
              <mat-icon>clear</mat-icon>
              Clear All
            </button>
          </div>
        </div>

      </div>
    </div>
  </div>

  <!-- Loading State -->
  <div class="loading-container" *ngIf="isLoading">
    <mat-spinner diameter="40"></mat-spinner>
    <p>Loading audit trail...</p>
  </div>

  <!-- Audit Table -->
  <div class="audit-table-container" *ngIf="!isLoading">
    <!-- No Results -->
    <div class="no-results" *ngIf="dataSource.data.length === 0">
      <mat-icon class="no-results-icon">search_off</mat-icon>
      <h3>No audit entries found</h3>
      <p>Try adjusting your filters or search criteria.</p>
    </div>

    <!-- Main Audit Table -->
    <div class="table-wrapper" *ngIf="dataSource.data.length > 0">
      <table mat-table [dataSource]="dataSource" class="audit-table" multiTemplateDataRows matSort>
        
        <!-- Timestamp Column -->
        <ng-container matColumnDef="timestamp">
          <th mat-header-cell *matHeaderCellDef mat-sort-header class="timestamp-header">Date</th>
          <td mat-cell *matCellDef="let entry" class="timestamp-cell">
            <div class="timestamp-display">
              <span class="time-ago">{{ getTimeAgo(entry.timestamp) }}</span>
              <span class="full-timestamp">{{ entry.timestamp | date:'MMM d, y, h:mm a' }}</span>
            </div>
          </td>
        </ng-container>

        <!-- User Column -->
        <ng-container matColumnDef="user">
          <th mat-header-cell *matHeaderCellDef mat-sort-header class="user-header">User</th>
          <td mat-cell *matCellDef="let entry" class="user-cell">
            <div class="user-info">
              <span class="user-name">{{ entry.userDisplayName }}</span>
            </div>
          </td>
        </ng-container>

        <!-- Action Column -->
        <ng-container matColumnDef="action">
          <th mat-header-cell *matHeaderCellDef class="action-header">Action</th>
          <td mat-cell *matCellDef="let entry" class="action-cell">
            <div class="action-display">
              <mat-icon class="action-icon" [style.color]="entry.actionColor">{{ entry.actionIcon }}</mat-icon>
              <span class="action-text">{{ entry.action }}</span>
            </div>
          </td>
        </ng-container>

        <!-- Action Description Column -->
        <ng-container matColumnDef="actionDescription">
          <th mat-header-cell *matHeaderCellDef class="action-description-header">Description</th>
          <td mat-cell *matCellDef="let entry" class="action-description-cell">
            <span class="action-description-text">{{ entry.actionDisplayName }}</span>
          </td>
        </ng-container>

        <!-- Entity Column -->
        <ng-container matColumnDef="entity">
          <th mat-header-cell *matHeaderCellDef class="entity-header">Record Name</th>
          <td mat-cell *matCellDef="let entry" class="entity-cell">
            <div class="entity-info">
              <span class="entity-name">{{ getEntityDisplayName(entry) }}</span>
              <span class="entity-type">{{ mapEntityTypeToDisplay(entry.entityType) }}</span>
            </div>
          </td>
        </ng-container>

        <!-- Identifier Column -->
        <ng-container matColumnDef="identifier">
          <th mat-header-cell *matHeaderCellDef class="identifier-header">Identifier</th>
          <td mat-cell *matCellDef="let entry" class="identifier-cell">
            <span class="identifier-text" [class.na-value]="!entry.identifier">{{ entry.identifier || '[N/A]' }}</span>
          </td>
        </ng-container>

        <!-- Keyway Column -->
        <ng-container matColumnDef="keyway">
          <th mat-header-cell *matHeaderCellDef class="keyway-header">Keyway</th>
          <td mat-cell *matCellDef="let entry" class="keyway-cell">
            <span class="keyway-text" [class.na-value]="!entry.keyway">{{ entry.keyway || '[N/A]' }}</span>
          </td>
        </ng-container>

        <!-- AppName Column -->
        <ng-container matColumnDef="appName">
          <th mat-header-cell *matHeaderCellDef class="app-name-header">App Name</th>
          <td mat-cell *matCellDef="let entry" class="app-name-cell">
            <span class="app-name-text" [class.na-value]="!entry.appName">{{ entry.appName || '[N/A]' }}</span>
          </td>
        </ng-container>

        <!-- Source Column -->
        <ng-container matColumnDef="source">
          <th mat-header-cell *matHeaderCellDef class="source-header">Source</th>
          <td mat-cell *matCellDef="let entry" class="source-cell">
            <span class="source-text">{{ entry.source }}</span>
          </td>
        </ng-container>

        <!-- Actions Column -->
        <ng-container matColumnDef="actions">
          <th mat-header-cell *matHeaderCellDef class="actions-header">Details</th>
          <td mat-cell *matCellDef="let entry" class="actions-cell">
            <mat-icon *ngIf="entry.hasDetails" 
                      class="expand-icon"
                      [class.rotated]="entry.isExpanded">
              expand_more
            </mat-icon>
            <span *ngIf="!entry.hasDetails" class="no-details-indicator">—</span>
          </td>
        </ng-container>

        <!-- Expandable Detail Row -->
        <ng-container matColumnDef="expandedDetail">
          <td mat-cell *matCellDef="let entry" [attr.colspan]="displayedColumns.length" class="detail-cell">
            <div class="detail-container" 
                 *ngIf="entry.isExpanded"
                 [@expandCollapse]>
              
              <!-- Field Details Table -->
              <div class="field-details-section" *ngIf="entry.hasDetails && getFilteredDetails(entry).length > 0">
                <h4 class="details-title">Field Details</h4>
                <table mat-table [dataSource]="getFilteredDetails(entry)" class="details-table">
                  
                  <!-- Field Name Column -->
                  <ng-container matColumnDef="fieldName">
                    <th mat-header-cell *matHeaderCellDef class="field-header">Field Name</th>
                    <td mat-cell *matCellDef="let detail" class="field-cell">
                      <div class="field-info">
                        <span class="field-name">{{ transformFieldDisplayName(detail.fieldDisplayName) }}</span>
                      </div>
                    </td>
                  </ng-container>

                  <!-- Previous Value Column -->
                  <ng-container matColumnDef="oldValue">
                    <th mat-header-cell *matHeaderCellDef class="old-value-header">Previous Value</th>
                    <td mat-cell *matCellDef="let detail" class="old-value-cell">
                      <!-- MECS Errors - show clickable icon with text -->
                      <ng-container *ngIf="isMecsErrorsField(detail.fieldName) && detail.oldValue; else regularOldValue">
                        <button mat-button
                                class="mecs-errors-button"
                                (click)="openMecsErrorsDialog(detail.oldValue, $event)"
                                matTooltip="Click to view error details">
                          <mat-icon>error_outline</mat-icon>
                          <span>View Errors</span>
                        </button>
                      </ng-container>

                      <ng-template #regularOldValue>
                        <span *ngIf="!detail.isSecured"
                              class="value-text"
                              [class.empty-value]="!detail.oldDisplayValue && !isBooleanField(detail)"
                              [class.shear-lines-value]="isShearLinesField(detail.fieldName)"
                              [class.shear-lines-empty]="isShearLinesField(detail.fieldName) && !detail.oldDisplayValue">
                          <ng-container *ngIf="isShearLinesField(detail.fieldName) && detail.oldDisplayValue; else checkKeyBitArrayOld">
                            <div class="shear-lines-grid">
                              <div class="shear-line-row" *ngFor="let row of formatShearLinesValue(detail.oldDisplayValue); let rowIndex = index">
                                <span class="shear-char"
                                      [class.grayed-out]="isColumnGrayedOut(entry, charIndex, true)"
                                      *ngFor="let char of row; let charIndex = index">{{ char }}</span>
                              </div>
                            </div>
                          </ng-container>
                          <ng-template #checkKeyBitArrayOld>
                            <ng-container *ngIf="isKeyBitArrayField(detail.fieldDisplayName) && detail.oldDisplayValue; else checkUserTargetValuesOld">
                              <div class="key-bit-array-grid">
                                <div class="key-bit-array-row" *ngFor="let row of formatKeyBitArrayValue(detail.oldDisplayValue)">
                                  <span class="key-bit-char" *ngFor="let char of row">{{ char }}</span>
                                </div>
                              </div>
                            </ng-container>
                          </ng-template>
                          <ng-template #checkUserTargetValuesOld>
                            <ng-container *ngIf="isUserTargetValuesField(detail.fieldName) && detail.oldDisplayValue; else checkRoleTargetValuesOld">
                              {{ formatUserTargetValues(detail.oldDisplayValue) }}
                            </ng-container>
                          </ng-template>
                          <ng-template #checkRoleTargetValuesOld>
                            <ng-container *ngIf="isRoleTargetValuesField(detail.fieldName) && detail.oldDisplayValue; else normalOldValue">
                              {{ formatRoleTargetValues(detail.oldDisplayValue) }}
                            </ng-container>
                          </ng-template>
                          <ng-template #normalOldValue>
                            {{ getDisplayValue(detail.oldDisplayValue, detail) }}
                          </ng-template>
                        </span>
                        <span *ngIf="detail.isSecured" class="secured-value">
                          <mat-icon class="security-icon">security</mat-icon>
                          ***SECURED***
                        </span>
                      </ng-template>
                    </td>
                  </ng-container>

                  <!-- New Value Column -->
                  <ng-container matColumnDef="newValue">
                    <th mat-header-cell *matHeaderCellDef class="new-value-header">New Value</th>
                    <td mat-cell *matCellDef="let detail" class="new-value-cell">
                      <span *ngIf="!detail.isSecured"
                            class="value-text"
                            [class.empty-value]="!detail.newDisplayValue && !isBooleanField(detail)"
                            [class.shear-lines-value]="isShearLinesField(detail.fieldName)"
                            [class.shear-lines-empty]="isShearLinesField(detail.fieldName) && !detail.newDisplayValue">
                        <ng-container *ngIf="isShearLinesField(detail.fieldName) && detail.newDisplayValue; else checkKeyBitArrayNew">
                          <div class="shear-lines-grid">
                            <div class="shear-line-row" *ngFor="let row of formatShearLinesValue(detail.newDisplayValue); let rowIndex = index">
                              <span class="shear-char"
                                    [class.grayed-out]="isColumnGrayedOut(entry, charIndex, false)"
                                    *ngFor="let char of row; let charIndex = index">{{ char }}</span>
                            </div>
                          </div>
                        </ng-container>
                        <ng-template #checkKeyBitArrayNew>
                          <ng-container *ngIf="isKeyBitArrayField(detail.fieldDisplayName) && detail.newDisplayValue; else checkUserTargetValuesNew">
                            <div class="key-bit-array-grid">
                              <div class="key-bit-array-row" *ngFor="let row of formatKeyBitArrayValue(detail.newDisplayValue)">
                                <span class="key-bit-char" *ngFor="let char of row">{{ char }}</span>
                              </div>
                            </div>
                          </ng-container>
                        </ng-template>
                        <ng-template #checkUserTargetValuesNew>
                          <ng-container *ngIf="isUserTargetValuesField(detail.fieldName) && detail.newDisplayValue; else checkRoleTargetValuesNew">
                            {{ formatUserTargetValues(detail.newDisplayValue) }}
                          </ng-container>
                        </ng-template>
                        <ng-template #checkRoleTargetValuesNew>
                          <ng-container *ngIf="isRoleTargetValuesField(detail.fieldName) && detail.newDisplayValue; else normalNewValue">
                            {{ formatRoleTargetValues(detail.newDisplayValue) }}
                          </ng-container>
                        </ng-template>
                        <ng-template #normalNewValue>
                          {{ getDisplayValue(detail.newDisplayValue, detail) }}
                        </ng-template>
                      </span>
                      <span *ngIf="detail.isSecured" class="secured-value">
                        <mat-icon class="security-icon">security</mat-icon>
                        ***SECURED***
                      </span>
                    </td>
                  </ng-container>

                  <!-- Define which columns to display in details table -->
                  <tr mat-header-row *matHeaderRowDef="detailsDisplayedColumns"></tr>
                  <tr mat-row *matRowDef="let row; columns: detailsDisplayedColumns;"></tr>
                </table>
              </div>

            </div>
          </td>
        </ng-container>

        <!-- Table Header and Row Definitions -->
        <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
        <tr mat-row *matRowDef="let row; columns: displayedColumns;"
            class="audit-row"
            [class.expanded-row]="row.isExpanded"
            [class.clickable-row]="row.hasDetails"
            (click)="row.hasDetails ? toggleExpanded(row) : null"
            [attr.aria-label]="row.hasDetails ? (row.isExpanded ? 'Collapse details' : 'Expand details') : 'No details available'">
        </tr>
        <tr mat-row *matRowDef="let row; columns: ['expandedDetail']; when: isExpandableRow"
            class="detail-row"
            [class.detail-row-expanded]="row.isExpanded">
        </tr>
      </table>
    </div>
  </div>

  <!-- Pagination -->
  <mat-paginator
    *ngIf="!isLoading && dataSource.data.length > 0"
    [length]="totalCount"
    [pageSize]="pageSize"
    [pageIndex]="currentPage"
    [pageSizeOptions]="pageSizeOptions"
    (page)="onPageChange($event)"
    showFirstLastButtons
    aria-label="Audit trail pagination">
  </mat-paginator>
</div>